import React, { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useNavigate, useLocation } from 'react-router-dom'
import { Paper } from '@mui/material'

import { TableUrlPagination } from '../../../../ui'
import { IP_TABLE_COLUMNS_GUILD, IP_TABLE_COLUMNS } from '../../constants'
import { ipTableStyles } from './ip-table.styles'
import { parseDateString } from '../../../../core/utils'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const IPTable = () => {
  const { formatMessage ,formatDate} = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    data,
    search,
    pagination: { total_registros: totalEntries },
    isLoading,
  } = useSelector((state) => state.ip)
  const combos = useSelector((state) => state.combos)
  const global = useSelector((state) => state.global)
  const isGuild = !!global.gremio

  const formattedColumns = useMemo(() => {
    const columns = isGuild ? IP_TABLE_COLUMNS_GUILD : IP_TABLE_COLUMNS
    return columns.map((column) => {
      // Base column configuration for Material React Table
      const baseColumn = {
        accessorKey: column.field,
        id: column.field,
        header: formatMessage({ id: `pages.ipList.table.columns.${column.field}.title` }),
        size: column.width,
        minSize: column.minWidth || column.width,
        maxSize: column.maxWidth,
        enableSorting: column.sorting !== false,
        muiTableHeadCellProps: {
          align: column.align || 'left',
        },
        muiTableBodyCellProps: {
          align: column.align || 'left',
          sx: {
            minWidth: column.minWidth || column.width,
          },
        },
      }

      // Add lookup functionality if combo data exists
      if (column.comboId && combos[column.comboId]?.data) {
        const lookup = getLookup(combos[column.comboId].data)
        baseColumn.Cell = ({ cell }) => {
          const value = cell.getValue()
          return lookup[value] || value || <span>--</span>
        }
      } else if (column.render && typeof column.render === 'function') {
        // Handle custom render function
        baseColumn.Cell = ({ row }) => {
          const value = column.render(row.original)
          return value || <span>--</span>
        }
      } else {
        // Default cell renderer with empty value handling
        baseColumn.Cell = ({ cell }) => {
          const value = cell.getValue()
          return value || <span>--</span>
        }
      }

      return baseColumn
    })
  }, [combos, isGuild, formatMessage])

  const processedData = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        fecha_creacion: formatDate(parseDateString(item.fecha_creacion)),
        fecha_inspeccion: formatDate(parseDateString(item.fecha_inspeccion)),
        fecha_factura: formatDate(parseDateString(item.fecha_factura)),
        fecha_estado: formatDate(parseDateString(item.fecha_estado)),
        fecha_creacion_ip: formatDate(parseDateString(item.fecha_creacion_ip)),
        tableData: {
          ...item.tableData,
        },
      })),
    [data]
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      tableLayout: 'fixed',
      draggable: false,
    }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const tableComponents = useMemo(
    () => ({
      Container: ({ children }) => <Paper elevation={0}>{children}</Paper>,
    }),
    []
  )

  const handleRowClick = useCallback((_, row) => {
    const props = {
      pathname: `/ip/${row.id}`,
      state: { prevSearch: location.search },
    }
    return navigate(props)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      components={tableComponents}
      data={processedData}
      isLoading={isLoading}
      onRowClick={handleRowClick}
    />
  )
}
